<template>
  <div>
    <div class="mb-24 space-y-32">
      <h1 class="heading-2">
        <FormattedMessage
          v-if="isAnUpgrade && data"
          :definition="translations.detailsTitleUpgraded"
        >
          <template #grade>
            <span class="bg-static-brand-hi">{{
              upgradeWording[data.replacementOrder.grade]
            }}</span>
          </template>
        </FormattedMessage>
        <template v-else>
          {{ i18n(translations.replacementOrderSectionTitle) }}
        </template>
      </h1>

      <XrReplacementOrderDetailsCard
        v-if="data?.replacementOrder"
        :grade-label="upgradeWording[data.replacementOrder.grade]"
        :image="data.replacementOrder.product.image"
        :is-an-upgrade
        :previous-grade-label="upgradeWording[data.initialOrder.grade]"
        :seller="data.replacementOrder.seller"
        :title="data.replacementOrder.product.title"
      />

      <div>
        <h2 class="heading-3 mb-12">
          {{ i18n(translations.informationSectionTitle) }}
        </h2>
        <p class="body-1 mb-16">
          {{ i18n(translations.informationSectionDescription) }}
        </p>
        <XrInformationSection
          v-if="data"
          :deadline="data.initialOrder.returnWindow"
          :delivery-date="data.replacementOrder.deliveryDatesWindow"
        />
      </div>
    </div>

    <RevButton
      class="hidden md:mb-12 md:inline-block"
      data-qa="continue-cta-desktop"
      data-test="continue-cta-desktop"
      full-width="always"
      variant="primary"
      @click="goToNextPage"
      >{{ i18n(translations.getStartedCta) }}</RevButton
    >
    <RevLink
      class="mx-auto mb-72 block w-fit md:mb-96"
      data-qa="faq-cta"
      data-test="faq-cta"
      target="_blank"
      :to="expressReplacementFaqArticleLink"
    >
      {{ i18n(translations.faqCta) }}
    </RevLink>

    <RevStickyBar class="block md:hidden">
      <RevButton
        data-qa="continue-cta-mobile"
        data-test="continue-cta-mobile"
        full-width="always"
        variant="primary"
        @click="goToNextPage"
        >{{ i18n(translations.getStartedCta) }}</RevButton
      >
    </RevStickyBar>
  </div>
</template>

<script setup lang="ts">
import {
  navigateTo,
  useAsyncData,
  useRouteLocationWithLocale,
  useRuntimeConfig,
  useZendeskFaqBaseUrl,
} from '#imports'
import { computed, inject, onMounted, toRaw } from 'vue'
import type { RouteLocationRaw } from 'vue-router'

import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { toNuxtLink } from '@backmarket/nuxt-module-navigation/toNuxtLink'
import { RevButton } from '@ds/components/Button'
import { RevLink } from '@ds/components/Link'
import { RevStickyBar } from '@ds/components/StickyBar'

import type { ExpressReplacementProductGrade } from '~/api/express-replacement/types/GetExpressReplacement.types'
import { isConfigDefined } from '~/scopes/care-commons/utils/isConfigDefined'
import XrInformationSection from '~/scopes/customer-care/resolution/components/ExpressReplacement/details/XrInformationSection/XrInformationSection.vue'
import gradeTranslations from '~/scopes/customer-care/resolution/components/ExpressReplacement/details/XrReplacementOrderDetailsCard/XrReplacementOrderDetailsCard.translations'
import XrReplacementOrderDetailsCard from '~/scopes/customer-care/resolution/components/ExpressReplacement/details/XrReplacementOrderDetailsCard/XrReplacementOrderDetailsCard.vue'
import { ResolutionFlowStateMachineKey } from '~/scopes/customer-care/resolution/symbols'
import { CUSTOMER_CARE } from '~/scopes/customer-care/route-names'

import translations from './ExpressReplacementDetails.translations'

const resolutionFlowStateMachine = inject(ResolutionFlowStateMachineKey)

const i18n = useI18n()
const { ZENDESK_EXPRESS_REPLACEMENT_FAQ_ARTICLE } = useRuntimeConfig().public
const { zendeskFaqBaseUrl } = useZendeskFaqBaseUrl()
const resolveLocalizedRoute = useRouteLocationWithLocale()
const $httpFetch = useHttpFetch()
const getXrDataKey = getAsyncDataKey(
  'express-replacement-details-get-xr-data',
  {
    orderlineId: resolutionFlowStateMachine?.state.value.context.publicId,
  },
)

const { data, error } = await useAsyncData(
  getXrDataKey,
  () =>
    $httpFetch('/express-replacement/funnel/:orderlineId', {
      pathParams: {
        orderlineId: String(
          resolutionFlowStateMachine?.state.value.context.publicId,
        ),
      },
    }),
  {
    immediate: !resolutionFlowStateMachine?.state.value.context.xrData,
    default: () => resolutionFlowStateMachine?.state.value.context.xrData,
  },
)

onMounted(() => {
  if (error.value) {
    resolutionFlowStateMachine?.send('ERROR')
  }
})

if (data.value && !resolutionFlowStateMachine?.state.value.context.xrData) {
  resolutionFlowStateMachine?.send({
    type: 'SET_XR_DATA',
    data: toRaw(data.value),
  })
}
const expressReplacementFaqArticleLink = toNuxtLink({
  type: 'external',
  href: `${zendeskFaqBaseUrl}/articles/${isConfigDefined(ZENDESK_EXPRESS_REPLACEMENT_FAQ_ARTICLE, 'ZENDESK_EXPRESS_REPLACEMENT_FAQ_ARTICLE')}`,
})

const isAnUpgrade = computed(
  () => data.value?.replacementOrder.grade !== data.value?.initialOrder.grade,
)

const upgradeWording: Record<ExpressReplacementProductGrade, string> = {
  ECO: i18n(gradeTranslations.gradeEco),
  VERY_GOOD: i18n(gradeTranslations.gradeVeryGood),
  FLAWLESS: i18n(gradeTranslations.gradeFlawless),
  PREMIUM: i18n(gradeTranslations.gradePremium),
}

const goToNextPage = async () =>
  await navigateTo(
    resolveLocalizedRoute({
      name: CUSTOMER_CARE.RESOLUTION.EXPRESS_REPLACEMENT.SHIPPING,
    }) as RouteLocationRaw,
  )
</script>
